'use client';

import { c as _c } from "react/compiler-runtime";
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';
import { Minus, Plus } from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import type { ALL_LOCATIONS_FOR_SEARCH_QUERY_RESULT } from '@/sanity/types';
import { Button } from '../../common/button';
type LocationsMapProps = {
  locations: ALL_LOCATIONS_FOR_SEARCH_QUERY_RESULT;
};
export default function LocationsMap(t0) {
  const $ = _c(12);
  const {
    locations
  } = t0;
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const markersRef = useRef(t1);
  let t2;
  if ($[1] !== locations) {
    t2 = locations.filter(_temp);
    $[1] = locations;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const validLocations = t2;
  let t3;
  let t4;
  if ($[3] !== validLocations) {
    t3 = () => {
      if (!mapRef.current || validLocations.length === 0) {
        return;
      }
      let cancelled = false;
      const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_BROWSER_API_KEY;
      if (!apiKey) {
        return;
      }
      setOptions({
        key: apiKey,
        v: "weekly"
      });
      const init = async () => {
        const [t5, t6, coreLib] = await Promise.all([importLibrary("maps") as Promise<{
          Map: new (el: HTMLElement, opts: unknown) => {
            fitBounds: (bounds: unknown) => void;
            setCenter: (position: {
              lat: number;
              lng: number;
            }) => void;
            setZoom: (zoom: number) => void;
          };
        }>, importLibrary("marker") as Promise<{
          AdvancedMarkerElement: new (opts: unknown) => unknown;
        }>, importLibrary("core") as Promise<{
          LatLngBounds: new () => {
            extend: (position: {
              lat: number;
              lng: number;
            }) => void;
          };
        }>]);
        const {
          Map
        } = t5;
        const {
          AdvancedMarkerElement
        } = t6;
        if (cancelled) {
          return;
        }
        const map = new Map(mapRef.current as HTMLElement, {
          disableDefaultUI: true,
          clickableIcons: false,
          mapId: "DEMO_MAP_ID"
        } as unknown);
        const bounds = new coreLib.LatLngBounds();
        const markers = [];
        validLocations.forEach(location_0 => {
          const coords_0 = (location_0 as any).coordinates as {
            lat: number;
            lng: number;
          };
          const position = {
            lat: coords_0.lat,
            lng: coords_0.lng
          };
          bounds.extend(position);
          const marker = new AdvancedMarkerElement({
            position,
            map
          } as unknown);
          markers.push(marker);
        });
        if (validLocations.length > 1) {
          map.fitBounds(bounds);
        } else {
          if (validLocations.length === 1) {
            const coords_1 = (validLocations[0] as any).coordinates as {
              lat: number;
              lng: number;
            };
            map.setCenter({
              lat: coords_1.lat,
              lng: coords_1.lng
            });
            map.setZoom(15);
          }
        }
        mapInstanceRef.current = map;
        markersRef.current = markers;
      };
      init().catch(_temp2);
      return () => {
        cancelled = true;
        markersRef.current.forEach(_temp3);
        markersRef.current = [];
      };
    };
    t4 = [validLocations];
    $[3] = validLocations;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  if (validLocations.length === 0) {
    return null;
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = delta => {
      const map_0 = mapInstanceRef.current as {
        getZoom?: () => number;
        setZoom?: (z: number) => void;
      } | null;
      const current = map_0?.getZoom ? map_0.getZoom() : undefined;
      if (map_0?.setZoom && typeof current === "number") {
        map_0.setZoom(current + delta);
      }
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const handleZoom = t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div ref={mapRef} className="w-full h-[422px] rounded-3xl overflow-hidden" />;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => handleZoom(1);
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Button size="icon" type="button" onClick={t7} aria-label="Zoom in"><Plus /></Button>;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => handleZoom(-1);
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="relative w-full overflow-hidden ">{t6}<div className="pointer-events-none absolute inset-0"><div className="pointer-events-auto absolute right-10 bottom-6 flex flex-col gap-2">{t8}<Button type="button" size="icon" onClick={t9} aria-label="Zoom out"><Minus /></Button></div></div></div>;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  return t10;
}
function _temp3(marker_0) {
  if (marker_0?.map) {
    marker_0.map = null;
  }
}
function _temp2() {}
function _temp(location) {
  const coords = (location as any).coordinates as {
    lat?: number;
    lng?: number;
  } | undefined;
  return coords?.lat != null && coords?.lng != null;
}